import { getRequest, postRequest, patchRequest, deleteRequest } from '../utils/axios';

export const projectsService = {
  listProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  checkSubdomain,
  analyzeProject,
  generateProject,
  deployProject,
  cancelDeploy,
  getProjectPreview,
  getProjectAnalytics,
  archiveProject,
  unarchiveProject,
  setCustomDomain,
  verifyCustomDomain,
  clearCustomDomain,
  getSidebarBadges,
};

function getProject(hash, onSuccess, onError) {
  return getRequest(
    `/api/v1/projects/${hash}/`,
    (response) => onSuccess?.(response),
    (error, response) => onError?.(error, response),
  );
}

function listProjects(params, onSuccess, onError) {
  // Back-compat: listProjects(onSuccess, onError)
  if (typeof params === "function") {
    onError = onSuccess;
    onSuccess = params;
    params = undefined;
  }
  const query = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    query.set(key, String(value));
  });
  const qs = query.toString();
  return getRequest(
    qs ? `/api/v1/projects/?${qs}` : "/api/v1/projects/",
    (response) => onSuccess?.(response),
    (error, response) => onError?.(error, response),
  );
}

function createProject(project, onSuccess, onError) {
  return postRequest(
    '/api/v1/projects/',
    typeof project === 'string' ? { name: project } : project,
    (response) => onSuccess?.(response),
    (error, response) => onError?.(error, response),
  );
}

function updateProject(hash, data, onSuccess, onError) {
  return patchRequest(
    `/api/v1/projects/${hash}/`,
    data,
    (response) => onSuccess?.(response),
    (error, response) => onError?.(error, response),
  );
}

function deleteProject(hash, confirmation, onSuccess, onError) {
  return deleteRequest(
    `/api/v1/projects/${hash}/`,
    (response) => onSuccess?.(response),
    (error, response) => onError?.(error, response),
    { confirmation },
  );
}

function checkSubdomain(subdomain, projectHash, onSuccess, onError) {
  const params = new URLSearchParams({ subdomain });
  if (projectHash) params.set("project", projectHash);
  return getRequest(
    `/api/v1/projects/check-subdomain/?${params.toString()}`,
    (response) => onSuccess?.(response),
    (error, response) => onError?.(error, response),
  );
}

function analyzeProject(hash, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/analyze/`, {}, onSuccess, onError);
}

function generateProject(hash, data, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/generate/`, data || {}, onSuccess, onError);
}

function deployProject(hash, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/deploy/`, {}, onSuccess, onError);
}

function cancelDeploy(hash, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/cancel-deploy/`, {}, onSuccess, onError);
}

function getProjectPreview(hash, onSuccess, onError) {
  return getRequest(`/api/v1/projects/${hash}/preview/`, onSuccess, onError);
}

function getProjectAnalytics(hash, params, onSuccess, onError) {
  const query = new URLSearchParams(params);
  return getRequest(`/api/v1/projects/${hash}/analytics/?${query.toString()}`, onSuccess, onError);
}

function archiveProject(hash, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/archive/`, {}, onSuccess, onError);
}

function unarchiveProject(hash, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/unarchive/`, {}, onSuccess, onError);
}

function setCustomDomain(hash, domain, onSuccess, onError) {
  return postRequest(`/api/v1/projects/${hash}/custom-domain/`, { domain }, onSuccess, onError);
}

function verifyCustomDomain(hash, domain, onSuccess, onError) {
  const query = domain ? `?domain=${encodeURIComponent(domain)}` : "";
  return getRequest(`/api/v1/projects/${hash}/custom-domain/${query}`, onSuccess, onError);
}

function clearCustomDomain(hash, onSuccess, onError) {
  return deleteRequest(`/api/v1/projects/${hash}/custom-domain/`, onSuccess, onError);
}

function getSidebarBadges(hash, onSuccess, onError) {
  return getRequest(`/api/v1/projects/${hash}/sidebar-badges/`, onSuccess, onError);
}
